/** Freelancer vetting status labels for UI (mirrors backend workflow). */

export const VETTING_PIPELINE_STEPS = [
  { key: "draft", label: "Draft" },
  { key: "submitted", label: "Submitted" },
  { key: "identity_review", label: "Identity Review" },
  { key: "professional_review", label: "Professional Review" },
  { key: "portfolio_assessment", label: "Portfolio Assessment" },
  { key: "quality_review", label: "Quality Review" },
  { key: "verified", label: "Approved" },
];

export const VETTING_STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  identity_review: "Identity Review",
  professional_review: "Professional Review",
  portfolio_assessment: "Portfolio Assessment",
  quality_review: "Quality Review",
  verified: "Approved",
  rejected: "Rejected",
  more_information_required: "More Information Requested",
  under_review: "In Review",
  none: "Not started",
};

export function normalizeVettingStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  if (!key || key === "none") return "draft";
  if (key === "under_review") return "identity_review";
  if (key === "approved") return "verified";
  return key;
}

export function vettingStatusLabel(status) {
  const key = normalizeVettingStatus(status);
  return VETTING_STATUS_LABELS[key] || key.replace(/_/g, " ");
}

export function isInReviewStatus(status) {
  const key = normalizeVettingStatus(status);
  return [
    "submitted",
    "identity_review",
    "professional_review",
    "portfolio_assessment",
    "quality_review",
  ].includes(key);
}

/** True only when the workflow status is verified (ignore stale is_verified flags). */
export function isVerifiedStatus(status, isVerified = false) {
  const key = normalizeVettingStatus(status);
  if (
    key === "draft" ||
    key === "submitted" ||
    key === "rejected" ||
    key === "more_information_required" ||
    isInReviewStatus(key)
  ) {
    return false;
  }
  return key === "verified" || Boolean(isVerified);
}

export function resolveVettingRowStatus(row) {
  return normalizeVettingStatus(row?.application_status || row?.vetting_status || row?.status);
}

/** True when the freelancer currently has an active Verified badge. */
export function isActiveVerifiedUser(user) {
  if (!user) return false;
  if (user.is_verified_active === true) return true;
  const status = normalizeVettingStatus(user.vetting_status);
  if (
    status === "draft" ||
    status === "submitted" ||
    status === "rejected" ||
    status === "more_information_required" ||
    isInReviewStatus(status)
  ) {
    return false;
  }
  const verified =
    status === "verified" ||
    user.is_verified === true ||
    user.is_verified === 1 ||
    user.is_verified === "1";
  if (!verified) return false;
  if (user.vetting_expires_at) {
    const end = new Date(user.vetting_expires_at);
    if (!Number.isNaN(end.getTime()) && end <= new Date()) return false;
  }
  return true;
}

/** Keep localStorage session in sync after admin vetting actions. */
export function syncVettingFlagsToLocalStorage(flags = {}) {
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) return;
    const userData = JSON.parse(raw);
    if (!userData?.user) return;
    if (flags.vetting_status != null) {
      userData.user.vetting_status = flags.vetting_status;
    }
    if (flags.is_verified != null) {
      userData.user.is_verified = Boolean(flags.is_verified);
    }
    if (flags.vetting_expires_at !== undefined) {
      userData.user.vetting_expires_at = flags.vetting_expires_at;
    }
    localStorage.setItem("userData", JSON.stringify(userData));
    window.dispatchEvent(new CustomEvent("liber-user-updated"));
  } catch {
    /* ignore */
  }
}
